/**
 * Quick entry.
 *
 * The whole design brief for this form is "amount, then save". Everything else
 * is optional and stays out of the way:
 *   - the amount field takes focus on mount and asks iOS for the decimal pad;
 *   - the category is suggested from local rules as soon as a merchant is typed,
 *     and the suggestion says *why* so it is never a black box;
 *   - the date and time default to now;
 *   - Save is reachable after a single field, one-handed, above the keyboard.
 */
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import {
  MoneyParseError,
  formatMoney,
  parseAmountToSen,
  rankMerchantSuggestions,
  suggestCategory,
  toLocalInputValue,
  fromLocalInputValue,
  type Category,
  type MerchantRule,
  type PaymentSource,
  type TransactionType,
} from '@wis/shared';
import type { MerchantHistoryEntry } from '../db/repository.js';
import { Button, Field, Segmented, Select, TextArea, TextInput } from './primitives.js';

export interface QuickEntryValues {
  amountSen: number;
  type: TransactionType;
  merchant: string;
  categoryId: string;
  accountLabel: string;
  occurredAt: string;
  note: string | null;
}

export interface QuickEntryFormProps {
  categories: readonly Category[];
  accounts: readonly { label: string }[];
  rules: readonly MerchantRule[];
  history: readonly MerchantHistoryEntry[];
  timeZone: string;
  defaultAccountLabel: string;
  defaultCategoryId: string;
  currency: string;
  /** Prefilled values, e.g. from a Shortcut's /capture URL or when editing. */
  initial?: Partial<QuickEntryValues>;
  submitLabel?: string;
  source?: PaymentSource;
  onSubmit: (values: QuickEntryValues) => Promise<void> | void;
  onCancel?: () => void;
  /** Extra controls rendered under the save button, e.g. Delete. */
  footer?: React.ReactNode;
  autoFocusAmount?: boolean;
}

const TYPE_OPTIONS: { value: TransactionType; label: string }[] = [
  { value: 'expense', label: 'Expense' },
  { value: 'refund', label: 'Refund' },
  { value: 'transfer', label: 'Transfer' },
  { value: 'income', label: 'Income' },
];

/**
 * Memoised on purpose.
 *
 * The ledger changes underneath this form constantly -- a sync tick, a live
 * query result, a background refresh -- and every one of those re-renders the
 * provider above. A re-render that lands while the browser is part-way through
 * delivering typed text can revert a controlled input, losing what the user
 * just entered. Nothing outside this form's own props can affect what it
 * renders, so nothing outside its own props should re-render it.
 */
function QuickEntryFormInner({
  categories,
  accounts,
  rules,
  history,
  timeZone,
  defaultAccountLabel,
  defaultCategoryId,
  currency,
  initial,
  submitLabel = 'Save',
  onSubmit,
  onCancel,
  footer,
  autoFocusAmount = true,
}: QuickEntryFormProps): JSX.Element {
  const amountRef = useRef<HTMLInputElement>(null);

  const [amountText, setAmountText] = useState(() =>
    initial?.amountSen ? (initial.amountSen / 100).toFixed(2) : '',
  );
  const [type, setType] = useState<TransactionType>(initial?.type ?? 'expense');
  const [merchant, setMerchant] = useState(initial?.merchant ?? '');
  const [categoryId, setCategoryId] = useState(initial?.categoryId ?? defaultCategoryId);
  const [categoryTouched, setCategoryTouched] = useState(Boolean(initial?.categoryId));
  const [accountLabel, setAccountLabel] = useState(initial?.accountLabel ?? defaultAccountLabel);
  const [occurredAtLocal, setOccurredAtLocal] = useState(() =>
    toLocalInputValue(initial?.occurredAt ?? new Date(), timeZone),
  );
  const [note, setNote] = useState(initial?.note ?? '');
  const [showDetails, setShowDetails] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!autoFocusAmount) return undefined;
    // A short delay lets the sheet finish animating before iOS raises the
    // keyboard. Two guards, both of which matter:
    //   - if something else already has focus, it belongs to the user and this
    //     must not steal it back;
    //   - if the amount field is already focused, calling focus() again is not
    //     harmless -- it interrupts text the user (or an automated test) is in
    //     the middle of entering.
    const timer = window.setTimeout(() => {
      const field = amountRef.current;
      const active = document.activeElement;
      if (!field || active === field) return;
      // The sheet parks focus on its own panel; taking it from there is the
      // whole point. Taking it from a control the user chose is not.
      const parked =
        !active || active === document.body || active.getAttribute('role') === 'dialog';
      if (parked) field.focus();
    }, 60);
    return () => window.clearTimeout(timer);
  }, [autoFocusAmount]);

  const parsedSen = useMemo(() => {
    if (!amountText.trim()) return null;
    try {
      return Math.abs(parseAmountToSen(amountText));
    } catch {
      return null;
    }
  }, [amountText]);

  /**
   * An amount that has been typed but cannot be read is explained straight
   * away. Leaving Save disabled with no reason is the worst of both worlds.
   */
  const amountProblem = useMemo(() => {
    if (!amountText.trim()) return undefined;
    try {
      const sen = Math.abs(parseAmountToSen(amountText));
      return sen === 0 ? 'Enter an amount greater than zero.' : undefined;
    } catch (parseError) {
      return parseError instanceof MoneyParseError
        ? parseError.message
        : 'Enter an amount like 12.90';
    }
  }, [amountText]);

  const suggestion = useMemo(
    () =>
      suggestCategory({
        merchant,
        type,
        rules,
        history: history.map((entry) => ({
          merchant: entry.merchant,
          categoryId: entry.categoryId,
          count: entry.count,
        })),
        defaultCategoryId,
      }),
    [merchant, type, rules, history, defaultCategoryId],
  );

  // The suggestion only moves the picker while the user has not chosen one.
  useEffect(() => {
    if (!categoryTouched) setCategoryId(suggestion.categoryId);
  }, [suggestion.categoryId, categoryTouched]);

  const merchantSuggestions = useMemo(
    () =>
      rankMerchantSuggestions(
        history.map((entry) => ({
          merchant: entry.merchant,
          categoryId: entry.categoryId,
          count: entry.count,
          lastUsedAt: entry.lastUsedAt,
        })),
        merchant,
        5,
      ),
    [history, merchant],
  );

  const suggestionReason = useMemo(() => {
    switch (suggestion.reason) {
      case 'user-rule':
        return `From your rule for "${suggestion.matchedOn}"`;
      case 'history':
        return `You usually file "${suggestion.matchedOn}" here`;
      case 'keyword':
        return `Matched "${suggestion.matchedOn}"`;
      case 'transfer-type':
        return 'Transfers are never counted as spending';
      default:
        return 'No rule matched, so this is the default';
    }
  }, [suggestion]);

  const canSave = parsedSen !== null && parsedSen > 0 && !saving;

  async function handleSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setError(undefined);

    let amountSen: number;
    try {
      amountSen = Math.abs(parseAmountToSen(amountText));
    } catch (parseError) {
      setError(
        parseError instanceof MoneyParseError
          ? parseError.message
          : 'Enter an amount like 12.90',
      );
      amountRef.current?.focus();
      return;
    }

    if (amountSen === 0) {
      setError('Enter an amount greater than zero.');
      amountRef.current?.focus();
      return;
    }

    let occurredAt: string;
    try {
      occurredAt = fromLocalInputValue(occurredAtLocal, timeZone);
    } catch {
      setError('That date and time could not be read.');
      return;
    }

    setSaving(true);
    try {
      await onSubmit({
        amountSen,
        type,
        merchant: merchant.trim(),
        categoryId,
        accountLabel: accountLabel.trim() || defaultAccountLabel,
        occurredAt,
        note: note.trim() || null,
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="field">
        <label className="field__label" htmlFor="quick-amount">
          Amount
        </label>
        <div className="amount-input">
          <span className="amount-input__prefix" aria-hidden="true">
            {currency === 'MYR' ? 'RM' : currency}
          </span>
          <input
            id="quick-amount"
            ref={amountRef}
            className="amount-input__field"
            // decimal asks iOS for the numeric keypad with a decimal separator,
            // which is exactly the keyboard this field wants.
            inputMode="decimal"
            enterKeyHint="done"
            type="text"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            placeholder="0.00"
            aria-describedby="quick-amount-hint"
            aria-invalid={error ? true : undefined}
            value={amountText}
            onChange={(event) => {
              // Accept only what can be part of a Ringgit amount.
              const next = event.target.value.replace(/[^0-9.,]/g, '');
              setAmountText(next);
              if (error) setError(undefined);
            }}
          />
        </div>
        {error ?? amountProblem ? (
          <p className="field__error" role="alert">
            {error ?? amountProblem}
          </p>
        ) : null}
        <p className="field__hint" id="quick-amount-hint">
          {parsedSen !== null
            ? `Saves as ${formatMoney(parsedSen, { currency })}`
            : 'Type the amount and press Save. Everything else is optional.'}
        </p>
      </div>

      <Field label="Merchant" hint={merchant ? suggestionReason : 'Optional'}>
        {(props) => (
          <TextInput
            {...props}
            value={merchant}
            autoComplete="off"
            autoCapitalize="words"
            enterKeyHint="done"
            placeholder="Kopi Kita"
            onChange={(event) => setMerchant(event.target.value)}
          />
        )}
      </Field>

      {/*
        Suggestions as tappable chips rather than a <datalist>.
        iOS Safari barely surfaces a datalist, and where it does it turns the
        field into a combobox that intercepts typing. A row of chips is one tap
        instead of eight characters, and it works the same everywhere.
      */}
      {merchantSuggestions.length > 0 ? (
        <div
          className="chip-row"
          style={{ marginTop: 'calc(var(--space-4) * -1 + var(--space-1))', marginBottom: 'var(--space-4)' }}
          role="group"
          aria-label="Merchants you have used before"
        >
          {merchantSuggestions.map((entry) => (
            <button
              key={entry.merchant}
              type="button"
              className="chip"
              aria-pressed={merchant === entry.merchant}
              onClick={() => setMerchant(entry.merchant)}
            >
              {entry.merchant}
            </button>
          ))}
        </div>
      ) : null}

      <Field label="Category">
        {(props) => (
          <Select
            {...props}
            value={categoryId}
            onChange={(event) => {
              setCategoryId(event.target.value);
              setCategoryTouched(true);
            }}
          >
            {categories
              .filter((category) => !category.archived || category.id === categoryId)
              .map((category) => (
                <option key={category.id} value={category.id}>
                  {category.icon} {category.name}
                </option>
              ))}
          </Select>
        )}
      </Field>

      <Button
        variant="accent"
        large
        block
        type="submit"
        disabled={!canSave}
        aria-disabled={!canSave}
      >
        {saving ? 'Saving...' : submitLabel}
      </Button>

      <div className="row" style={{ marginTop: 'var(--space-3)' }}>
        <Button
          variant="quiet"
          className="grow"
          aria-expanded={showDetails}
          onClick={() => setShowDetails((open) => !open)}
        >
          {showDetails ? 'Hide details' : 'More details'}
        </Button>
        {onCancel ? (
          <Button variant="quiet" className="grow" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
      </div>

      {showDetails ? (
        <div className="stack" style={{ marginTop: 'var(--space-4)' }}>
          <div>
            <span className="field__label">Type</span>
            <Segmented
              label="Transaction type"
              value={type}
              options={TYPE_OPTIONS}
              onChange={(next) => {
                setType(next);
                if (!categoryTouched && next === 'transfer') setCategoryId('transfers');
              }}
            />
            {type !== 'expense' ? (
              <p className="field__hint">
                {type === 'refund'
                  ? 'A refund reduces your spending without changing the original payment.'
                  : 'Not counted as spending.'}
              </p>
            ) : null}
          </div>

          <Field label="Paid with">
            {(props) => (
              <Select
                {...props}
                value={accountLabel}
                onChange={(event) => setAccountLabel(event.target.value)}
              >
                {[...new Set([accountLabel, ...accounts.map((account) => account.label)])]
                  .filter(Boolean)
                  .map((label) => (
                    <option key={label} value={label}>
                      {label}
                    </option>
                  ))}
              </Select>
            )}
          </Field>

          <Field label="Date and time">
            {(props) => (
              <TextInput
                {...props}
                type="datetime-local"
                value={occurredAtLocal}
                onChange={(event) => setOccurredAtLocal(event.target.value)}
              />
            )}
          </Field>

          <Field label="Note" hint="Kept on this device unless cloud sync is switched on.">
            {(props) => (
              <TextArea
                {...props}
                value={note}
                rows={2}
                maxLength={500}
                onChange={(event) => setNote(event.target.value)}
              />
            )}
          </Field>
        </div>
      ) : null}

      {footer}
    </form>
  );
}

export const QuickEntryForm = memo(QuickEntryFormInner);
